import { useState, useEffect, useContext } from 'react'; // 1. Import useContext
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext'; // 2. Import AuthContext

const TABS = [ 'General', 'Business Info', 'Contact', 'Social Media', 'Verification', 'SEO', 'Images' ];

const BusinessForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const { user } = useContext(AuthContext); // 3. Get the logged-in user

  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    content: {
      general: { slug: '', stage: 'Prospect', status: '', tagline: '', short_description: '', description: '', product_description: '' },
      business_info: { contact_person: '', establishment_year: '', working_hours: '', type_of_business: '', nature_of_business: '', num_employees: '', annual_turnover: '', business_location: '', google_verified_link: '' },
      contact: { address: '', pincode: '', latitude: '', longitude: '', whatsapp: '', mobile: '', phone: '', contact_email: '', alt_email: '', video_link: '', website_url: '' },
      social: { facebook_url: '', twitter_url: '', linkedin_url: '', instagram_url: '', youtube_url: '' },
      verification: { cert_inc_text: '', pan_number: '', gstin_number: '' },
      seo: { title: '', tags: '', meta_description: '' }
    }
  });

  useEffect(() => {
    if (isEditing) {
      const fetchBusiness = async () => {
        try {
          // Use different endpoints based on user role
          const endpoint = user?.role === 'super_admin' ? `/super_admin/business/${id}` : `/user/business/${id}`;
          const response = await api.get(endpoint);
          const { name, subdomain, content } = response.data;
          setFormData(prevData => ({ ...prevData, name: name || '', subdomain: subdomain || '', content: { ...prevData.content, ...content } }));
        } catch (error) {
          console.error("Failed to fetch business data", error);
          navigate('/');
        }
      };
      fetchBusiness();
    }
  }, [id, isEditing, navigate]);

  const handleChange = (e) => {
    const { name, value, dataset } = e.target;
    const { section } = dataset;
    if (section) {
      setFormData(prev => ({ ...prev, content: { ...prev.content, [section]: { ...prev.content[section], [name]: value } } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // === THIS IS THE MAIN FIX ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Determine endpoints and return path based on user role
    const isSuperAdmin = user && user.role === 'super_admin';
    const createEndpoint = isSuperAdmin ? '/crm/business/create_lead' : '/user/business/create';
    const updateEndpoint = isSuperAdmin ? `/crm/business/update/${id}` : `/user/business/update/${id}`; // Example
    const returnPath = isSuperAdmin ? '/crm/leads/businesses' : '/';

    const payload = { name: formData.name, subdomain: formData.subdomain, content: formData.content };

    try {
      if (isEditing) {
        await api.put(updateEndpoint, payload);
      } else {
        await api.post(createEndpoint, payload);
      }
      navigate(returnPath);
    } catch (error) {
      console.error("Failed to save business", error);
      alert(error.response?.data?.message || "An error occurred.");
    }
  };

  const renderInput = (name, label, section, options = {}) => (
    <div className={options.className || "md:col-span-1"}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type={options.type || 'text'} name={name} data-section={section} value={formData.content[section]?.[name] || ''} onChange={handleChange} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500" {...options} />
    </div>
  );

  const renderTextarea = (name, label, section, options = {}) => (
    <div className={options.className || "md:col-span-2"}>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <textarea name={name} data-section={section} value={formData.content[section]?.[name] || ''} onChange={handleChange} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500" rows={options.rows || 3} {...options} />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">{isEditing ? 'Edit Business Listing' : 'Add New Business Listing'}</h1>
      <form onSubmit={handleSubmit}>
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-4 overflow-x-auto" aria-label="Tabs">
            {TABS.map(tab => (
              <button type="button" key={tab} onClick={() => setActiveTab(tab)} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${ activeTab === tab ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300' }`}>
                {tab}
              </button>
            ))}
          </nav>
        </div>
        <div className="bg-white p-6 rounded-b-lg shadow-md">
          <div className={`${activeTab === 'General' ? 'block' : 'hidden'}`}>
             <h3 className="text-xl font-semibold mb-4">General Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-1"><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label><input type="text" name="name" value={formData.name} onChange={handleChange} required className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"/></div>
               <div className="md:col-span-1"><label className="block text-sm font-medium text-gray-700 mb-1">Subdomain *</label><div className="flex items-center"><input type="text" name="subdomain" value={formData.subdomain} onChange={handleChange} required disabled={isEditing} className="block w-full px-3 py-2 border border-gray-300 rounded-l-md shadow-sm disabled:bg-gray-100"/><span className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 rounded-r-md">.saffronpages.com</span></div></div>
              {renderInput('slug', 'Slug (URL friendly name)', 'general')}
              {renderInput('tagline', 'Tagline', 'general', {className: 'md:col-span-2'})}
              {renderTextarea('short_description', 'Short Description', 'general', {className: 'md:col-span-2'})}
            </div>
          </div>
          <div className={`${activeTab === 'Business Info' ? 'block' : 'hidden'}`}>
             <h3 className="text-xl font-semibold mb-4">Business Information</h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{renderInput('contact_person', 'Contact Person', 'business_info')}{renderInput('establishment_year', 'Establishment Year', 'business_info', {type: 'number'})}{renderInput('working_hours', 'Working Hours', 'business_info')}{renderInput('google_verified_link', 'Google Verified Link', 'business_info', {type: 'url', className: 'md:col-span-2'})}</div>
          </div>
          <div className={`${activeTab === 'Contact' ? 'block' : 'hidden'}`}>
             <h3 className="text-xl font-semibold mb-4">Contact Information</h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{renderInput('address', 'Address', 'contact', {className: 'md:col-span-2'})}{renderInput('website_url', 'Website/Blog URL', 'contact', {type: 'url', className: 'md:col-span-2'})}</div>
          </div>
           <div className={`${activeTab === 'Social Media' ? 'block' : 'hidden'}`}>
             <h3 className="text-xl font-semibold mb-4">Social Media Information</h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{renderInput('facebook_url', 'Facebook URL', 'social', {type: 'url'})}{renderInput('instagram_url', 'Instagram URL', 'social', {type: 'url'})}</div>
          </div>
          <div className={`${['Verification', 'SEO', 'Images'].includes(activeTab) ? 'block' : 'hidden'}`}>
            <h3 className="text-xl font-semibold mb-4">{activeTab} Information</h3>
            <p className="text-gray-600">This section is a placeholder for the {activeTab.toLowerCase()} fields.</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-4">
          <button type="button" onClick={() => navigate(user.role === 'super_admin' ? '/crm/leads/businesses' : '/')} className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
          <button type="submit" className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700">Save Listing</button>
        </div>
      </form>
    </div>
  );
};

export default BusinessForm;